// src/pages/AdminApplications.tsx: admin review, approval, contract and invoice
// for manager applications. Rendered as the "Applications" tab inside Admin.tsx.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../services/supabase';
import { checkRegulator, firmNamesMatch } from '../lib/regulatory';
import {
  C, PRICING, ManagerApplication, ApplicationTeamMember,
  APP_STATUS_LABELS, appStatusColor, REG_STATUS_LABELS, regStatusColor,
  jurisdictionLabel, fundTypeLabel, fmtDate, fmtDateTime, gbp,
  sendMail, nextInvoiceNumber,
} from '../lib/applications';
import {
  generateContractHTML, generateInvoiceHTML, contractDataFromApp, invoiceDataFromApp,
} from '../lib/generatePDF';
import { uploadAndSign, fetchDocHtml } from '../lib/applicationDocs';
import DocumentModal from '../components/DocumentModal';

const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
const inputStyle: React.CSSProperties = {
  width: '100%', background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '3px', color: C.text, fontSize: '13px', padding: '8px 10px',
  outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px',
  textTransform: 'uppercase', display: 'block', marginBottom: '5px',
};
const btnPrimary: React.CSSProperties = {
  background: C.navy, color: C.white, border: 'none', borderRadius: '3px',
  padding: '9px 18px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
};
const btnGhost: React.CSSProperties = {
  background: C.white, color: C.textSec, border: `1px solid ${C.border}`,
  borderRadius: '3px', padding: '8px 15px', fontSize: '12px', fontWeight: 600,
  cursor: 'pointer', fontFamily: 'system-ui',
};

const STATUS_FILTERS = ['all', ...Object.keys(APP_STATUS_LABELS)];
const REG_FILTERS = ['all', 'verified', 'partial', 'not_found', 'manual_review', 'error', 'pending'];

function Badge({ text, tone }: { text: string; tone: { fg: string; bg: string; bd: string } }) {
  return (
    <span style={{
      background: tone.bg, color: tone.fg, border: `1px solid ${tone.bd}`,
      fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '2px',
      letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap',
    }}>{text}</span>
  );
}

export default function AdminApplications() {
  const [apps, setApps]       = useState<ManagerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [selected, setSelected] = useState<ManagerApplication | null>(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [regFilter, setRegFilter]       = useState('all');
  const [search, setSearch]             = useState('');

  const load = async () => {
    setLoading(true); setError('');
    const { data, error: err } = await supabase.from('manager_applications')
      .select('*').order('created_at', { ascending: false });
    if (err) { setError(err.message); setLoading(false); return; }
    setApps((data as ManagerApplication[]) || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return apps.filter(a => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (regFilter !== 'all' && (a.regulatory_status || 'pending') !== regFilter) return false;
      if (q && !`${a.firm_name} ${a.contact_email} ${a.contact_name}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [apps, statusFilter, regFilter, search]);

  return (
    <div>
      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '9px 12px', marginBottom: '14px', fontSize: '12.5px', color: C.danger }}>{error}</div>
      )}

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search firm name or email…" style={{ ...inputStyle, maxWidth: '260px' }} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inputStyle, maxWidth: '190px', cursor: 'pointer' }}>
          {STATUS_FILTERS.map(s => <option key={s} value={s}>{s === 'all' ? 'All statuses' : APP_STATUS_LABELS[s]}</option>)}
        </select>
        <select value={regFilter} onChange={e => setRegFilter(e.target.value)} style={{ ...inputStyle, maxWidth: '190px', cursor: 'pointer' }}>
          {REG_FILTERS.map(s => <option key={s} value={s}>{s === 'all' ? 'All regulatory' : REG_STATUS_LABELS[s]}</option>)}
        </select>
        <span style={{ fontSize: '12px', color: C.muted, fontWeight: 600 }}>{filtered.length} application{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div style={{ ...card, padding: '40px', textAlign: 'center', fontSize: '13px', color: C.muted }}>Loading applications…</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '6px' }}>No applications</div>
          <div style={{ fontSize: '13px', color: C.muted }}>{apps.length === 0 ? 'No one has applied yet.' : 'Nothing matches these filters.'}</div>
        </div>
      ) : (
        <div style={{ ...card, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr style={{ background: C.navy }}>
                {['Firm', 'Contact', 'Regulatory', 'Reg. Check', 'Status', 'AUM', 'Submitted', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '9px 12px', fontSize: '9.5px', color: 'rgba(255,255,255,0.7)', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => (
                <tr key={a.id} style={{ borderBottom: `1px solid ${C.borderLt}`, background: i % 2 === 0 ? C.white : C.surface }}>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: C.navy }}>{a.firm_name}</div>
                    {a.is_emerging_manager && (
                      <div style={{ marginTop: '3px' }}><Badge text="Emerging Manager" tone={{ fg: C.amber, bg: '#FFFBEB', bd: '#FDE68A' }} /></div>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: C.textSec }}>
                    <div>{a.contact_name}</div>
                    <div style={{ color: C.muted, fontSize: '11.5px' }}>{a.contact_email}</div>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: C.textSec }}>
                    <div>{a.jurisdiction}</div>
                    <div style={{ color: C.muted, fontSize: '11.5px', fontFamily: '"Courier New", monospace' }}>{a.regulatory_number}</div>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <Badge text={REG_STATUS_LABELS[a.regulatory_status || 'pending'] || '-'} tone={regStatusColor(a.regulatory_status)} />
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <Badge text={APP_STATUS_LABELS[a.status] || a.status} tone={appStatusColor(a.status)} />
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: C.textSec }}>{a.aum_range || '-'}</td>
                  <td style={{ padding: '10px 12px', fontSize: '11.5px', color: C.muted }}>{fmtDate(a.created_at)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <button onClick={() => setSelected(a)} style={{ ...btnGhost, padding: '5px 12px' }}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <ApplicationDetail
          app={selected}
          onClose={() => setSelected(null)}
          onChanged={(updated) => {
            setApps(prev => prev.map(a => a.id === updated.id ? updated : a));
            setSelected(updated);
          }}
        />
      )}
    </div>
  );
}

// ── Detail ───────────────────────────────────────────────────────────────────
function ApplicationDetail({ app, onClose, onChanged }: {
  app: ManagerApplication; onClose: () => void; onChanged: (a: ManagerApplication) => void;
}) {
  const [team, setTeam]     = useState<ApplicationTeamMember[]>([]);
  const [notes, setNotes]   = useState(app.admin_notes || '');
  const [busy, setBusy]     = useState('');
  const [msg, setMsg]       = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [approving, setApproving] = useState(false);
  const [rechecking, setRechecking] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('application_team_members').select('*')
        .eq('application_id', app.id).order('is_primary_contact', { ascending: false });
      setTeam((data as ApplicationTeamMember[]) || []);
    })();
  }, [app.id]);

  const patch = async (fields: Partial<ManagerApplication>, label: string) => {
    setBusy(label); setMsg(null);
    const { data, error } = await supabase.from('manager_applications')
      .update({ ...fields, updated_at: new Date().toISOString() })
      .eq('id', app.id).select().single();
    setBusy('');
    if (error) { setMsg({ type: 'err', text: error.message }); return null; }
    onChanged(data as ManagerApplication);
    return data as ManagerApplication;
  };

  const saveNotes = async () => {
    const r = await patch({ admin_notes: notes }, 'notes');
    if (r) setMsg({ type: 'ok', text: 'Notes saved.' });
  };

  const requestMoreInfo = async () => {
    const r = await patch({ status: 'more_info_requested', admin_notes: notes, reviewed_at: new Date().toISOString() }, 'info');
    if (!r) return;
    await sendMail('application_more_info', {
      contact_email: app.contact_email, contact_name: app.contact_name, admin_notes: notes,
    });
    setMsg({ type: 'ok', text: 'Applicant asked for more information.' });
  };

  const decline = async () => {
    if (!window.confirm(`Decline the application from ${app.firm_name}? The applicant will be emailed.`)) return;
    const r = await patch({ status: 'declined', admin_notes: notes, reviewed_at: new Date().toISOString() }, 'decline');
    if (!r) return;
    await sendMail('application_declined', {
      contact_email: app.contact_email, contact_name: app.contact_name, firm_name: app.firm_name,
    });
    setMsg({ type: 'ok', text: 'Application declined.' });
  };

  const rerunCheck = async () => {
    setRechecking(true);
    const reg = await checkRegulator(app.jurisdiction, app.regulatory_number);
    await patch({
      regulatory_status: reg.matchConfidence, regulatory_data: reg.rawData,
      regulator_firm_name: reg.firmName, regulator_url: reg.regulatorUrl,
    }, 'recheck');
    setRechecking(false);
  };

  const markVerified = async () => {
    const r = await patch({ regulatory_status: 'verified' }, 'verify');
    if (r) setMsg({ type: 'ok', text: 'Marked as verified.' });
  };

  // Activation: flip the applicant's profile to approved and email everyone.
  // Reachable both after the applicant signs in their portal and, for a
  // wet-signed agreement returned by email, straight from 'contract_sent'.
  const markSigned = async () => {
    const alreadySigned = !!app.contract_signed_at;
    if (!window.confirm(alreadySigned
      ? 'Activate this account? The applicant has already signed.'
      : 'Mark the contract as signed and activate this account?')) return;
    setBusy('sign');
    const now = new Date().toISOString();
    const { data, error } = await supabase.from('manager_applications')
      .update({
        status: 'approved',
        contract_signed_at: app.contract_signed_at || now,
        updated_at: now,
      })
      .eq('id', app.id).select().single();
    if (error) { setBusy(''); setMsg({ type: 'err', text: error.message }); return; }

    if (app.profile_id) {
      const { error: profErr } = await supabase.from('profiles')
        .update({ account_status: 'approved', role: 'manager' }).eq('id', app.profile_id);
      if (profErr) setMsg({ type: 'err', text: `Application updated but the profile did not activate: ${profErr.message}` });
    }

    await sendMail('application_welcome', {
      contact_email: app.contact_email, contact_name: app.contact_name, firm_name: app.firm_name,
    });
    for (const m of team.filter(t => !t.is_primary_contact)) {
      await sendMail('team_member_invite', {
        member_email: m.email, member_name: m.name,
        firm_name: app.firm_name, inviter_name: app.contact_name,
      });
    }
    await supabase.from('application_team_members')
      .update({ invited_at: now }).eq('application_id', app.id).is('invited_at', null);

    setBusy('');
    onChanged(data as ManagerApplication);
    setMsg({ type: 'ok', text: 'Account activated and welcome emails sent.' });
  };

  const regTone = regStatusColor(app.regulatory_status);
  const nameMatch = firmNamesMatch(app.firm_name, app.regulator_firm_name);

  return (
    <Overlay onClose={onClose}>
      <div className="no-print" style={{ padding: '22px 26px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '21px', color: C.navy, fontWeight: 400, margin: '0 0 6px' }}>{app.firm_name}</h2>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <Badge text={APP_STATUS_LABELS[app.status] || app.status} tone={appStatusColor(app.status)} />
            <Badge text={REG_STATUS_LABELS[app.regulatory_status || 'pending'] || '-'} tone={regTone} />
            {app.is_emerging_manager && <Badge text="Emerging Manager" tone={{ fg: C.amber, bg: '#FFFBEB', bd: '#FDE68A' }} />}
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', color: C.mutedLt, cursor: 'pointer', lineHeight: 1 }}>×</button>
      </div>

      <div className="no-print" style={{ padding: '22px 26px' }}>
        {msg && (
          <div style={{
            background: msg.type === 'ok' ? '#F0FDF4' : '#FEF2F2',
            border: `1px solid ${msg.type === 'ok' ? '#BBF7D0' : '#FECACA'}`,
            borderRadius: '3px', padding: '9px 12px', marginBottom: '16px',
            fontSize: '12.5px', color: msg.type === 'ok' ? C.success : C.danger,
          }}>{msg.text}</div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>

          {/* Left, application data */}
          <div>
            <SectionTitle>Application</SectionTitle>
            <KV k="Contact" v={app.contact_name} />
            <KV k="Email" v={app.contact_email} />
            <KV k="Phone" v={app.contact_phone} />
            <KV k="Heard about us" v={app.heard_about} />
            <KV k="Website" v={app.firm_website} />
            <KV k="Headquarters" v={[app.firm_city, app.firm_country].filter(Boolean).join(', ')} />
            <KV k="Fund type" v={fundTypeLabel(app.fund_type)} />
            <KV k="Strategy" v={app.strategy} />
            <KV k="AUM range" v={app.aum_range} />
            <KV k="Number of funds" v={app.fund_count} />
            {app.is_emerging_manager && (
              <>
                <KV k="Previous firm" v={app.previous_firm_name} />
                <KV k="Previous reg. no." v={app.previous_regulatory_number} />
              </>
            )}
            <KV k="Submitted" v={fmtDateTime(app.created_at)} />
            {app.firm_description && (
              <div style={{ marginTop: '10px', fontSize: '12.5px', color: C.textSec, lineHeight: 1.6 }}>{app.firm_description}</div>
            )}

            <SectionTitle style={{ marginTop: '20px' }}>Team ({team.length})</SectionTitle>
            {team.map(m => (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12.5px' }}>
                <div>
                  <span style={{ color: C.text, fontWeight: 500 }}>{m.name}</span>
                  <span style={{ color: C.mutedLt, marginLeft: '8px', fontSize: '11.5px' }}>{m.email}</span>
                </div>
                <span style={{ color: C.muted, fontSize: '11.5px' }}>{m.is_primary_contact ? 'Primary' : m.role}</span>
              </div>
            ))}
          </div>

          {/* Right, regulatory verification */}
          <div>
            <SectionTitle>Regulatory verification</SectionTitle>
            <KV k="Jurisdiction" v={jurisdictionLabel(app.jurisdiction)} />
            <KV k="Number" v={app.regulatory_number} />
            <KV k="Status" v={<Badge text={REG_STATUS_LABELS[app.regulatory_status || 'pending'] || '-'} tone={regTone} />} />

            <div style={{ ...card, marginTop: '12px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: C.surface }}>
                    <th style={{ textAlign: 'left', padding: '7px 10px', fontSize: '9.5px', color: C.muted, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Field</th>
                    <th style={{ textAlign: 'left', padding: '7px 10px', fontSize: '9.5px', color: C.muted, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Applicant said</th>
                    <th style={{ textAlign: 'left', padding: '7px 10px', fontSize: '9.5px', color: C.muted, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Regulator shows</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderTop: `1px solid ${C.borderLt}` }}>
                    <td style={{ padding: '8px 10px', color: C.muted }}>Firm name</td>
                    <td style={{ padding: '8px 10px', color: C.text }}>{app.firm_name}</td>
                    <td style={{ padding: '8px 10px', color: app.regulator_firm_name ? (nameMatch ? C.success : C.danger) : C.mutedLt, fontWeight: 600 }}>
                      {app.regulator_firm_name || 'No record'}
                    </td>
                  </tr>
                  <tr style={{ borderTop: `1px solid ${C.borderLt}` }}>
                    <td style={{ padding: '8px 10px', color: C.muted }}>Status</td>
                    <td style={{ padding: '8px 10px', color: C.mutedLt }}>-</td>
                    <td style={{ padding: '8px 10px', color: C.text }}>{app.regulatory_data?.Data?.[0]?.Status || (app.regulatory_status === 'verified' ? 'Active' : '-')}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {app.regulator_url && (
              <a href={app.regulator_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: '10px', fontSize: '12px', color: C.teal, textDecoration: 'none' }}>
                View on the {app.jurisdiction} register ↗
              </a>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
              <button onClick={rerunCheck} disabled={rechecking} style={{ ...btnGhost, opacity: rechecking ? 0.6 : 1 }}>
                {rechecking ? 'Checking…' : 'Re-run check'}
              </button>
              {app.regulatory_status !== 'verified' && (
                <button onClick={markVerified} disabled={busy === 'verify'} style={btnGhost}>Mark as Verified</button>
              )}
            </div>

            <SectionTitle style={{ marginTop: '22px' }}>Admin notes</SectionTitle>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
              placeholder="Internal notes. Included in the email if you request more information." />
            <button onClick={saveNotes} disabled={busy === 'notes'} style={{ ...btnGhost, marginTop: '8px' }}>
              {busy === 'notes' ? 'Saving…' : 'Save notes'}
            </button>
          </div>
        </div>

        {/* ── Documents ── */}
        {(app.status === 'contract_sent' || app.status === 'contract_signed' || app.status === 'approved') && (
          <div style={{ borderTop: `1px solid ${C.border}`, marginTop: '24px', paddingTop: '18px' }}>
            <SectionTitle>Documents</SectionTitle>
            <DocLinks app={app} />
            {app.contract_signed_at && (
              <div style={{ marginTop: '12px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '3px', padding: '10px 13px', fontSize: '12.5px', color: C.success, lineHeight: 1.6 }}>
                <strong>Signed</strong> by {app.contract_signed_by_name || app.contact_name}
                {app.contract_signed_by_title ? `, ${app.contract_signed_by_title}` : ''} on {fmtDateTime(app.contract_signed_at)}
                {app.contract_signed_url ? ' via the Altveno platform.' : '.'}
              </div>
            )}
          </div>
        )}

        {/* ── Decisions ── */}
        <div style={{ borderTop: `1px solid ${C.border}`, marginTop: '24px', paddingTop: '18px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {!['contract_sent', 'contract_signed', 'approved'].includes(app.status) && (
            <>
              <button onClick={requestMoreInfo} disabled={!!busy} style={btnGhost}>Request More Information</button>
              <button onClick={decline} disabled={!!busy} style={{ ...btnGhost, color: C.danger, borderColor: `${C.danger}44` }}>Decline</button>
              <button onClick={() => setApproving(true)} disabled={!!busy} style={btnPrimary}>Approve</button>
            </>
          )}
          {(app.status === 'contract_sent' || app.status === 'contract_signed') && (
            <>
              <button onClick={markSigned} disabled={busy === 'sign'} style={{ ...btnPrimary, background: C.success }}>
                {busy === 'sign' ? 'Activating…'
                  : app.status === 'contract_signed' ? 'Activate Account' : 'Mark as Signed and Activate'}
              </button>
              {app.status === 'contract_sent' && (
                <span style={{ fontSize: '12px', color: C.muted }}>
                  Waiting for the applicant to sign in their portal. Use this if they return a wet signature instead.
                </span>
              )}
            </>
          )}
          {app.status === 'approved' && (
            <div style={{ fontSize: '12.5px', color: C.success, fontWeight: 600 }}>
              ✓ Activated{app.contract_signed_at ? ` on ${fmtDate(app.contract_signed_at)}` : ''}
            </div>
          )}
        </div>
      </div>

      {approving && (
        <ApprovalFlow app={app} team={team} onClose={() => setApproving(false)} onSent={a => { onChanged(a); setApproving(false); }} />
      )}
    </Overlay>
  );
}

// ── Approval flow ────────────────────────────────────────────────────────────
function ApprovalFlow({ app, team, onClose, onSent }: {
  app: ManagerApplication; team: ApplicationTeamMember[];
  onClose: () => void; onSent: (a: ManagerApplication) => void;
}) {
  const [step, setStep] = useState(1);
  const [contractType, setContractType] = useState<'standard' | 'emerging'>(app.is_emerging_manager ? 'emerging' : 'standard');
  const [price, setPrice]               = useState(String(app.is_emerging_manager ? PRICING.emerging : PRICING.standard));
  const [fundsIncluded, setFunds]       = useState('1');
  const [addFundPrice, setAddFundPrice] = useState(String(PRICING.additionalFund));
  const [maxUsers, setMaxUsers]         = useState(String(PRICING.maxUsers));
  const [specialTerms, setSpecialTerms] = useState('');
  const [startDate, setStartDate]       = useState(new Date().toISOString().slice(0, 10));
  const [invoiceNo, setInvoiceNo]       = useState<string>(app.invoice_number || '');
  const [sendTo, setSendTo]             = useState(app.contact_email);
  const [sending, setSending]           = useState(false);
  const [err, setErr]                   = useState('');
  const [result, setResult]             = useState<{
    app: ManagerApplication; contractUrl: string | null; invoiceUrl: string | null; warning: string;
  } | null>(null);

  const previewRef = useRef<HTMLIFrameElement>(null);

  const extraFunds = Math.max(0, (app.fund_count || 1) - (parseInt(fundsIncluded) || 1));
  const subtotal = (parseFloat(price) || 0) + extraFunds * (parseFloat(addFundPrice) || 0);
  const vat = 0; // Placeholder until VAT treatment is confirmed.
  const total = subtotal + vat;
  const dueDate = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

  // The unsaved form state as an application row, so the preview and the
  // stored document are generated from identical inputs.
  const draftApp: ManagerApplication = useMemo(() => ({
    ...app,
    contract_type: contractType,
    contract_price: parseFloat(price) || 0,
    funds_included: parseInt(fundsIncluded) || 1,
    additional_fund_price: parseFloat(addFundPrice) || 0,
    max_users: parseInt(maxUsers) || PRICING.maxUsers,
    special_terms: specialTerms.trim() || null,
    contract_start_date: startDate,
    invoice_number: invoiceNo || null,
  }), [app, contractType, price, fundsIncluded, addFundPrice, maxUsers, specialTerms, startDate, invoiceNo]);

  const contractHTML = useMemo(() => generateContractHTML(contractDataFromApp(draftApp)), [draftApp]);
  const invoiceHTML  = useMemo(() => generateInvoiceHTML(invoiceDataFromApp(draftApp, dueDate)), [draftApp, dueDate]);

  // Allocate the invoice number once, when the invoice step is first opened.
  useEffect(() => {
    if (step === 3 && !invoiceNo) { nextInvoiceNumber().then(setInvoiceNo); }
  }, [step, invoiceNo]);

  const send = async () => {
    setSending(true); setErr('');
    const now = new Date().toISOString();

    // 1. Save the agreed terms first, so the documents are generated from the
    //    same row the applicant will later read back when they sign.
    const { data, error } = await supabase.from('manager_applications').update({
      status: 'contract_sent',
      contract_type: contractType,
      contract_price: parseFloat(price) || 0,
      funds_included: parseInt(fundsIncluded) || 1,
      additional_fund_price: parseFloat(addFundPrice) || 0,
      max_users: parseInt(maxUsers) || PRICING.maxUsers,
      special_terms: specialTerms.trim() || null,
      contract_start_date: startDate,
      invoice_number: invoiceNo || null,
      contract_sent_at: now,
      reviewed_at: now,
      updated_at: now,
    }).eq('id', app.id).select().single();
    if (error) { setErr(error.message); setSending(false); return; }
    let saved = data as ManagerApplication;

    // 2. Generate the two documents and file them in the private bucket. A
    //    storage failure must not strand the approval, so it degrades to a
    //    warning and the admin sends the printed copies themselves.
    let contractUrl: string | null = null;
    let invoiceUrl: string | null = null;
    let warning = '';
    try {
      const contract = await uploadAndSign(
        saved.id, 'contract', generateContractHTML(contractDataFromApp(saved)));
      const invoice = await uploadAndSign(
        saved.id, 'invoice', generateInvoiceHTML(invoiceDataFromApp(saved, dueDate)));
      contractUrl = contract.url;
      invoiceUrl = invoice.url;

      const { data: withUrls } = await supabase.from('manager_applications')
        .update({
          contract_pdf_url: contractUrl, invoice_pdf_url: invoiceUrl,
          updated_at: new Date().toISOString(),
        })
        .eq('id', saved.id).select().single();
      if (withUrls) saved = withUrls as ManagerApplication;
    } catch (e: any) {
      warning = `The documents could not be stored: ${e?.message || e}. The applicant has been emailed, `
        + 'but the email carries no document links and the portal will not show them. '
        + 'Check that the "documents" storage bucket exists, then re-send.';
    }

    // 3. Email the applicant with links to whatever was stored.
    await sendMail('application_contract_sent', {
      contact_email: sendTo, contact_name: app.contact_name,
      firm_name: app.firm_name, invoice_number: invoiceNo, total: gbp(total),
      // The email links to the in-platform viewer, which regenerates the
      // documents from this row.
      application_id: saved.id,
    });

    setSending(false);
    setResult({ app: saved, contractUrl, invoiceUrl, warning });
  };

  const stepTitles = ['Contract terms', 'Preview contract', 'Generate invoice', 'Send'];

  return (
    <Overlay onClose={onClose} inner>
      <div className="no-print" style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '18px', color: C.navy, fontWeight: 400, margin: '0 0 3px' }}>Approve {app.firm_name}</h3>
          <div style={{ fontSize: '11.5px', color: C.muted }}>Step {step} of 4, {stepTitles[step - 1]}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', color: C.mutedLt, cursor: 'pointer', lineHeight: 1 }}>×</button>
      </div>

      <div style={{ padding: '22px 24px' }}>
        {err && <div className="no-print" style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '9px 12px', marginBottom: '14px', fontSize: '12.5px', color: C.danger }}>{err}</div>}

        {/* Step 1, terms */}
        {step === 1 && (
          <div className="no-print">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={labelStyle}>Contract type</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={contractType}
                  onChange={e => {
                    const v = e.target.value as 'standard' | 'emerging';
                    setContractType(v);
                    setPrice(String(v === 'emerging' ? PRICING.emerging : PRICING.standard));
                  }}>
                  <option value="standard">Standard Manager</option>
                  <option value="emerging">Emerging Manager</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Annual fee (GBP)</label>
                <input style={inputStyle} type="number" value={price} onChange={e => setPrice(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Funds included</label>
                <input style={inputStyle} type="number" min="1" value={fundsIncluded} onChange={e => setFunds(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Additional fund price (GBP)</label>
                <input style={inputStyle} type="number" value={addFundPrice} onChange={e => setAddFundPrice(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Maximum users</label>
                <input style={inputStyle} type="number" min="1" value={maxUsers} onChange={e => setMaxUsers(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Contract start date</label>
                <input style={inputStyle} type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
            </div>
            <div style={{ marginTop: '14px' }}>
              <label style={labelStyle}>Special terms (optional)</label>
              <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical', lineHeight: 1.6 }} value={specialTerms} onChange={e => setSpecialTerms(e.target.value)} />
            </div>
            <div style={{ marginTop: '14px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '12px 14px', fontSize: '12.5px', color: C.textSec }}>
              The applicant listed <strong>{app.fund_count || 1}</strong> fund{(app.fund_count || 1) !== 1 ? 's' : ''}
              {extraFunds > 0 ? `, ${extraFunds} beyond the included allowance, charged at ${gbp(parseFloat(addFundPrice) || 0)} each.` : '.'}
              {' '}Total: <strong>{gbp(total)}</strong>
            </div>
          </div>
        )}

        {/* Steps 2 and 3 preview the exact documents that will be stored,
            emailed and signed, same generator, same data. */}
        {step === 2 && <DocPreview ref={previewRef} title="Platform Access Agreement" html={contractHTML} />}
        {step === 3 && <DocPreview ref={previewRef} title="Invoice" html={invoiceHTML} />}

        {/* Step 4, send */}
        {step === 4 && !result && (
          <div className="no-print">
            <div style={{ fontSize: '13px', color: C.textSec, lineHeight: 1.7, marginBottom: '16px' }}>
              This marks the application as <strong>Contract Sent</strong>, files the agreement and invoice
              in the private documents store, and emails the applicant links to view and sign them online.
            </div>
            <div style={{ maxWidth: '360px', marginBottom: '16px' }}>
              <label style={labelStyle}>Send to</label>
              <input style={inputStyle} value={sendTo} onChange={e => setSendTo(e.target.value)} />
            </div>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '12px 14px', fontSize: '12.5px', color: C.textSec, marginBottom: '18px' }}>
              <div>Invoice <strong>{invoiceNo || '-'}</strong></div>
              <div>Total <strong>{gbp(total)}</strong> · due {fmtDate(dueDate)}</div>
            </div>
            <button onClick={send} disabled={sending || !sendTo.trim()} style={{ ...btnPrimary, opacity: sending ? 0.7 : 1 }}>
              {sending ? 'Generating and sending…' : 'Send Contract and Invoice'}
            </button>
          </div>
        )}

        {/* Step 4, confirmation */}
        {result && (
          <div className="no-print">
            <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '3px', padding: '12px 15px', marginBottom: '14px', fontSize: '13px', color: C.success, lineHeight: 1.6 }}>
              <strong>Sent.</strong> {app.firm_name} is now marked as Contract Sent and {sendTo} has been
              emailed the agreement and invoice.
            </div>
            {result.warning && (
              <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '3px', padding: '11px 14px', marginBottom: '14px', fontSize: '12.5px', color: C.amber, lineHeight: 1.6 }}>
                {result.warning}
              </div>
            )}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '14px 16px', fontSize: '12.5px', color: C.textSec, lineHeight: 1.9 }}>
              <div>Invoice <strong>{invoiceNo || '-'}</strong> · Total <strong>{gbp(total)}</strong> · due {fmtDate(dueDate)}</div>
              {/* The viewer route, not the storage URL: a signed storage link
                  downloads the raw HTML rather than rendering it. */}
              <div style={{ display: 'flex', gap: '14px', marginTop: '6px', flexWrap: 'wrap' }}>
                <a href={`/documents/${result.app.id}/contract`} target="_blank" rel="noopener noreferrer"
                  style={{ color: C.teal, textDecoration: 'none', fontWeight: 600 }}>View agreement ↗</a>
                <a href={`/documents/${result.app.id}/invoice`} target="_blank" rel="noopener noreferrer"
                  style={{ color: C.teal, textDecoration: 'none', fontWeight: 600 }}>View invoice ↗</a>
              </div>
            </div>
            <div style={{ fontSize: '12.5px', color: C.muted, marginTop: '14px', lineHeight: 1.6 }}>
              The applicant signs in their portal. You will see the status change to <strong>Contract Signed</strong>,
              at which point you can activate the account.
            </div>
            <button onClick={() => onSent(result.app)} style={{ ...btnPrimary, marginTop: '16px' }}>Done</button>
          </div>
        )}
      </div>

      {/* Once sent, the wizard is read-only: the confirmation panel owns the exit. */}
      {!result && (
      <div className="no-print" style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
        <button onClick={() => step === 1 ? onClose() : setStep(s => s - 1)} style={btnGhost}>
          {step === 1 ? 'Cancel' : 'Back'}
        </button>
        <div style={{ display: 'flex', gap: '8px' }}>
          {(step === 2 || step === 3) && (
            <button onClick={() => previewRef.current?.contentWindow?.print()} style={btnGhost}>Print / Save as PDF</button>
          )}
          {step < 4 && <button onClick={() => setStep(s => s + 1)} style={btnPrimary}>Continue</button>}
        </div>
      </div>
      )}
    </Overlay>
  );
}

// == Stored document links ====================================================
// Signed URLs expire, so the panel mints fresh ones on open and falls back to
// whatever was saved on the row.
function DocLinks({ app }: { app: ManagerApplication }) {
  const [viewing, setViewing] = useState<null | 'contract' | 'invoice' | 'signed'>(null);
  const [signedHtml, setSignedHtml] = useState<string | null>(null);
  const [loadingSigned, setLoadingSigned] = useState(false);

  // Regenerated from the row rather than fetched: a signed storage URL is
  // served as a download, so the admin never actually sees the document.
  const dueDate = useMemo(() => {
    const sent = app.contract_sent_at ? new Date(app.contract_sent_at) : new Date();
    return new Date(sent.getTime() + 30 * 86400000).toISOString().slice(0, 10);
  }, [app.contract_sent_at]);
  const contractHTML = useMemo(() => generateContractHTML(contractDataFromApp(app)), [app]);
  const invoiceHTML = useMemo(() => generateInvoiceHTML(invoiceDataFromApp(app, dueDate)), [app, dueDate]);

  // The countersigned copy holds the drawn signature, which only exists in the
  // stored file.
  const openSigned = async () => {
    setViewing('signed');
    if (signedHtml) return;
    setLoadingSigned(true);
    setSignedHtml(await fetchDocHtml(app.id, 'contract-signed'));
    setLoadingSigned(false);
  };

  if (!app.contract_sent_at) {
    return (
      <div style={{ fontSize: '12.5px', color: C.muted }}>
        No documents yet. They are generated when the contract is sent.
      </div>
    );
  }

  const placeholder = (message: string) =>
    `<!DOCTYPE html><html><head><meta charset="utf-8"></head>` +
    `<body style="font-family:system-ui,sans-serif;color:#6B7280;padding:60px;text-align:center;">` +
    `${message}</body></html>`;

  return (
    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
      <button onClick={() => setViewing('contract')} style={btnGhost}>Platform Access Agreement</button>
      <button onClick={() => setViewing('invoice')} style={btnGhost}>
        {`Invoice ${app.invoice_number || ''}`.trim()}
      </button>
      {app.contract_signed_at && (
        <button onClick={openSigned} style={btnGhost}>Signed Agreement</button>
      )}

      {viewing === 'contract' && (
        <DocumentModal html={contractHTML}
          title={`Platform Access Agreement, ${app.firm_name}`}
          onClose={() => setViewing(null)} />
      )}
      {viewing === 'invoice' && (
        <DocumentModal html={invoiceHTML}
          title={`Invoice ${app.invoice_number || ''}`.trim()}
          onClose={() => setViewing(null)} />
      )}
      {viewing === 'signed' && (
        <DocumentModal
          html={signedHtml || placeholder(loadingSigned
            ? 'Loading the signed agreement...'
            : 'The signed agreement could not be loaded from storage.')}
          title={`Signed Agreement, ${app.firm_name}`}
          onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

// == Document preview =========================================================
// The agreement and invoice are generated as complete HTML documents in
// lib/generatePDF. Previewing them in an iframe means the admin reviews the
// exact bytes that get stored, emailed and signed, rather than a second,
// drifting React rendering of the same contract.
const DocPreview = React.forwardRef<HTMLIFrameElement, { html: string; title: string }>(
  function DocPreview({ html, title }, ref) {
    return (
      <iframe
        ref={ref}
        title={title}
        srcDoc={html}
        style={{
          width: '100%', height: '460px', border: `1px solid ${C.border}`,
          borderRadius: '3px', background: C.white, display: 'block',
        }}
      />
    );
  }
);

// ── Shared chrome ────────────────────────────────────────────────────────────
function Overlay({ children, onClose, inner }: { children: React.ReactNode; onClose: () => void; inner?: boolean }) {
  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '32px 20px', zIndex: inner ? 210 : 200, overflowY: 'auto',
      }}
    >
      <div style={{
        background: C.white, borderRadius: '4px', width: '100%',
        maxWidth: inner ? '760px' : '980px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>{children}</div>
    </div>
  );
}

function SectionTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '10px', ...style }}>{children}</div>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: '12px', padding: '6px 0', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12.5px' }}>
      <div style={{ width: '135px', flexShrink: 0, color: C.muted }}>{k}</div>
      <div style={{ color: C.text, fontWeight: 500, wordBreak: 'break-word' }}>{v || '-'}</div>
    </div>
  );
}
